import * as React from "react";
import { Outlet, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@ozpanel/ui";
import { useModules } from "./modules";
import { Sidebar, buildNav } from "./shell/Sidebar";
import { Topbar, type Notice } from "./shell/Topbar";
import { LiveAlerts } from "./shell/LiveAlerts";
import { LicenceBanner } from "./shell/LicenceBanner";
import { useAccountsApi } from "./accountsApi";

// AppShell — topbar (brand, notifications, theme, account menu) over a sidebar + content
// grid. The nav is generated from the live module registry (Manifest.Category/Tiers), never a
// hand-maintained array.

export function AppShell() {
  const { who } = useAuth();
  const api = useModules();
  const accounts = useAccountsApi();
  const modules = useQuery({ queryKey: ["modules"], queryFn: api.list });
  // Drives the notification centre: real, measured facts, not a placeholder feed.
  const backups = useQuery({ queryKey: ["backup-status"], queryFn: accounts.backupStatus, retry: false });

  const groups = React.useMemo(
    () => buildNav(modules.data?.items ?? [], who?.role ?? ""),
    [modules.data, who?.role],
  );

  // STANDING CONDITIONS — things that are wrong RIGHT NOW and stay wrong until someone acts.
  //
  // Bus events used to be folded in here too, and that was a straight duplication: main.go
  // installs `srv.Realtime().WithSink(… notificationsMod.Record …)`, so everything reaching the
  // live stream is also written to the durable feed — which is what the NOTIFICATION BELL beside
  // this shows. A single warn event therefore appeared in both controls at once, each with its
  // own red badge, and the two were rendered as identical bell icons side by side. "Why do I
  // have two notification bells" is the only reasonable reaction to that.
  //
  // What is left here is the half the feed genuinely cannot carry: a module that IS degraded and
  // an account with NO backup are states, not occurrences. Nothing will emit them again, so a
  // feed of things that happened will not show them, and they must not be lost.
  const notices: Notice[] = React.useMemo(() => {
    const out: Notice[] = [];
    for (const m of modules.data?.items ?? []) {
      if (m.health === "critical" || m.health === "degraded") {
        out.push({
          id: `mod:${m.manifest.name}`,
          title: `${m.manifest.title} is ${m.health}`,
          detail: m.manifest.description,
          tone: m.health === "critical" ? "crit" : "warn",
          to: `/modules/${m.manifest.name}`,
        });
      }
    }
    for (const t of backups.data?.unprotected ?? []) {
      out.push({
        id: `backup:${t}`,
        title: `${t} has no successful backup`,
        detail: "No snapshot on record for this account.",
        tone: "crit",
        to: "/modules/backups",
      });
    }
    // Worst first — a critical buried under nine warnings is a critical nobody sees.
    return out.sort((a, b) => (a.tone === "crit" ? -1 : 0) - (b.tone === "crit" ? -1 : 0));
  }, [modules.data, backups.data]);

  const degraded = notices.length;

  // The sidebar is 232px of fixed width — more than two thirds of a 320px phone, leaving the
  // content a column too narrow to read. On small screens it becomes a drawer instead: off-canvas
  // by default, over the content when opened, and it closes on navigation because a nav panel
  // that stays open after you have used it hides the thing you navigated to.
  const [navOpen, setNavOpen] = React.useState(false);
  const location = useLocation();
  React.useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <div className="flex h-dvh flex-col">
      <Topbar notices={notices} onMenu={() => setNavOpen((v) => !v)} />
      <LiveAlerts />
      <LicenceBanner />
      <div className="relative flex min-h-0 flex-1">
        {/* Off-canvas below lg, in the flow at lg and above. Rendered once either way, so the
            nav's own state and queries are not torn down when the viewport changes. */}
        <div
          className={
            "absolute inset-y-0 left-0 z-40 bg-bg transition-transform lg:static lg:z-auto lg:translate-x-0 " +
            (navOpen ? "translate-x-0 shadow-[0_0_60px_rgba(0,0,0,.5)]" : "-translate-x-full")
          }
        >
          <Sidebar groups={groups} loading={modules.isLoading} degradedCount={degraded} />
        </div>
        {navOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 z-30 bg-black/50 lg:hidden"
            onClick={() => setNavOpen(false)}
          />
        )}
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1120px] px-4 py-5 pb-24 sm:px-7 sm:py-7">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
